import React, { useEffect, useState, useRef } from 'react';
import { DotsThreeVertical } from '@phosphor-icons/react';
import { cn } from '../../lib/utils';
export interface ActionMenuItem {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  destructive?: boolean;
  disabled?: boolean;
  /** Optional content rendered on the right side of the item (e.g. confirmation state) */
  rightSlot?: React.ReactNode;
  /** If true, item closes the menu when clicked. Default: true. Set false for items that need to stay open briefly (e.g. copy link). */
  closeOnClick?: boolean;
}
interface ActionMenuProps {
  items: ActionMenuItem[];
  triggerAriaLabel?: string;
  align?: 'left' | 'right';
  className?: string;
}
export function ActionMenu({
  items,
  triggerAriaLabel = 'Acțiuni',
  align = 'right',
  className
}: ActionMenuProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);
  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);
  const handleItemClick = (item: ActionMenuItem) => {
    if (item.disabled) return;
    item.onClick();
    if (item.closeOnClick !== false) {
      setOpen(false);
    }
  };
  return <div ref={containerRef} className={cn('relative', className)} data-id="element-2791">
      <button type="button" onClick={() => setOpen(v => !v)} aria-label={triggerAriaLabel} aria-haspopup="menu" aria-expanded={open} className={cn('inline-flex items-center justify-center w-9 h-9 rounded-md transition-colors cursor-pointer', open ? 'bg-dark-3 text-light-1' : 'text-light-2 hover:text-light-1 hover:bg-dark-3')} data-id="element-2792">
        <DotsThreeVertical size={20} weight="bold" data-id="element-2793" />
      </button>

      {open && <div role="menu" className={cn('absolute top-full mt-1.5 z-50 min-w-[220px] max-w-[calc(100vw-32px)] p-1 rounded-xl bg-dark-2 border border-dark-4 shadow-2xl', 'animate-in fade-in slide-in-from-top-1 duration-150', align === 'right' ? 'right-0' : 'left-0')} data-id="element-2794">
          {items.map((item, idx) => <button key={`${item.label}-${idx}`} role="menuitem" type="button" onClick={() => handleItemClick(item)} disabled={item.disabled} className={cn('w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left', item.disabled ? 'text-light-4 cursor-not-allowed' : item.destructive ? 'text-red-1 hover:bg-red-4 cursor-pointer' : 'text-light-1 hover:bg-dark-3 cursor-pointer')} data-id="element-2795">
              <span className={cn('shrink-0 inline-flex items-center justify-center', item.destructive ? 'text-red-1' : 'text-light-2')} data-id="element-2796">
                {item.icon}
              </span>
              <span className="flex-1 truncate" data-id="element-2797">{item.label}</span>
              {item.rightSlot && <span className="shrink-0 ml-2" data-id="element-2798">{item.rightSlot}</span>}
            </button>)}
        </div>}
    </div>;
}